import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { normalizeGroupName } from '@/lib/category-groups'
import { getDbOrThrow } from '@/lib/firebase'
import { mapAttribute } from '@/lib/mappers'
import type { AttributeType, CostType, MaterialCategoryAttribute } from '@/lib/types'

/**
 * Categories (`materialCategories`) and their configurable attributes
 * (`materialCategoriesAttrs`, linked by `categoryId`). Both are admin-only for writes — enforced by
 * the security rules, not here.
 */
/** What the editor sets. The order is not here: it is assigned on create and kept on edit. */
export type CategoryDraft = {
  name: string
  nameI18n: Record<string, string>
  costType: CostType
  /** Display-only group ('' = none); normalised on save. */
  groupName: string
}

export type AttributeDraft = {
  categoryId: string
  name: string
  nameI18n: Record<string, string>
  code: string
  type: AttributeType
  orderIndex: number
}

/**
 * Creates or updates a category. `createdBy`/`createdByName`/`createdAt` are written only when the
 * document does not exist yet, so repeated edits keep the original authorship — the existence check
 * mirrors `FirestoreMaterialCategoryRepository.saveCategory`.
 *
 * Order: a new category goes to the END of the list (largest `orderIndex` + 1, read fresh); an edit
 * leaves `orderIndex` untouched. The old free-text `code` is no longer written (nothing reads it;
 * the link is the document id).
 */
export async function saveCategory(
  id: string | null,
  draft: CategoryDraft,
  actor: { uid: string; label: string },
): Promise<void> {
  const db = getDbOrThrow()
  const ref = id === null ? doc(collection(db, 'materialCategories')) : doc(db, 'materialCategories', id)
  const base = {
    name: draft.name,
    nameI18n: draft.nameI18n,
    costType: draft.costType,
    groupName: normalizeGroupName(draft.groupName),
    updatedBy: actor.uid,
    updatedByName: actor.label,
    updatedAt: serverTimestamp(),
  }

  const exists = id !== null && (await getDoc(ref)).exists()
  if (exists) {
    await setDoc(ref, base, { merge: true })
    return
  }
  await setDoc(ref, {
    ...base,
    orderIndex: await nextCategoryOrder(),
    createdBy: actor.uid,
    createdByName: actor.label,
    createdAt: serverTimestamp(),
  })
}

/**
 * Deletes a category together with all of its attributes in ONE atomic batch — the same cascade the
 * app performs, so no orphan attribute rows are left behind.
 */
export async function deleteCategoryCascade(id: string): Promise<void> {
  const db = getDbOrThrow()
  const attrs = await getDocs(
    query(collection(db, 'materialCategoriesAttrs'), where('categoryId', '==', id)),
  )
  const batch = writeBatch(db)
  batch.delete(doc(db, 'materialCategories', id))
  for (const item of attrs.docs) batch.delete(item.ref)
  await batch.commit()
}

/** Live attributes of one category, sorted in memory (orderIndex, then name) — no index needed. */
export function subscribeToAttributes(
  categoryId: string,
  onData: (items: MaterialCategoryAttribute[]) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(collection(getDbOrThrow(), 'materialCategoriesAttrs'), where('categoryId', '==', categoryId)),
    (snapshot) => {
      const items = snapshot.docs
        .map((item) => mapAttribute(item.id, item.data()))
        .filter((item): item is MaterialCategoryAttribute => item !== null)
        .sort((left, right) =>
          left.orderIndex !== right.orderIndex
            ? left.orderIndex - right.orderIndex
            : left.name.localeCompare(right.name, 'ru'),
        )
      onData(items)
    },
    onError,
  )
}

/**
 * Live attributes of EVERY category (the whole `materialCategoriesAttrs` collection — small, and
 * readable by any signed-in user). Used by the consistency checks.
 */
export function subscribeToAllAttributes(
  onData: (items: MaterialCategoryAttribute[]) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    collection(getDbOrThrow(), 'materialCategoriesAttrs'),
    (snapshot) =>
      onData(
        snapshot.docs
          .map((item) => mapAttribute(item.id, item.data()))
          .filter((item): item is MaterialCategoryAttribute => item !== null),
      ),
    onError,
  )
}

/** Attributes carry no authorship fields in the app, so the document is written as a whole. */
export function saveAttribute(id: string | null, draft: AttributeDraft): Promise<void> {
  const db = getDbOrThrow()
  const ref =
    id === null ? doc(collection(db, 'materialCategoriesAttrs')) : doc(db, 'materialCategoriesAttrs', id)
  return setDoc(ref, {
    categoryId: draft.categoryId,
    name: draft.name,
    nameI18n: draft.nameI18n,
    code: draft.code,
    type: draft.type,
    orderIndex: draft.orderIndex,
  })
}

export function deleteAttribute(id: string): Promise<void> {
  return deleteDoc(doc(getDbOrThrow(), 'materialCategoriesAttrs', id))
}

/** The order a new category gets: after the last one (largest `orderIndex` + 1; 0 for the first). */
async function nextCategoryOrder(): Promise<number> {
  const snapshot = await getDocs(collection(getDbOrThrow(), 'materialCategories'))
  let max = -1
  for (const item of snapshot.docs) {
    const value = item.data().orderIndex
    // A missing / non-numeric order reads as 0 everywhere (app and console), so it counts as 0 here.
    max = Math.max(max, typeof value === 'number' && Number.isFinite(value) ? value : 0)
  }
  return Math.floor(max) + 1
}

/**
 * Writes a new `orderIndex` per category (renumbering from «Несоответствия»). Only `orderIndex` is
 * touched (`update`: a category deleted meanwhile fails instead of being recreated); batches of 400,
 * each atomic.
 */
export async function setCategoryOrder(updates: { id: string; orderIndex: number }[]): Promise<void> {
  const db = getDbOrThrow()
  for (let start = 0; start < updates.length; start += 400) {
    const batch = writeBatch(db)
    for (const update of updates.slice(start, start + 400)) {
      batch.update(doc(db, 'materialCategories', update.id), { orderIndex: update.orderIndex })
    }
    await batch.commit()
  }
}
